// Signed-in identity and what the user may do.
//
// One API call answers all of it: `GET /me` returns the person, their internal
// step roles per workspace, their company approval authority from the
// assignments table, and explicit capability flags. The old client assembled
// this from four sources and parsed role out of team names; none of that comes
// across — the server returns roles.
//
// Two rules worth stating out loud:
//   1. A profile that fails to load FAILS CLOSED. `loadSession` throws and the
//      shell renders an error. The old client failed open (every step lit)
//      because a second gate re-checked server-side; the API is now the only
//      gate, so failing open would be a hole.
//   2. Everything here is an optimistic hint for the UI — it disables a control
//      the API would refuse instead of erroring on click. The API enforces
//      independently and is the authority.
import { getClient } from '../api/client'
import type {
  AnalyticsSection,
  Profile,
  ProfileAssignment,
  ReferenceSection,
  RulesSection,
} from '../api/types'
import type { CurrentUser, Invoice } from './types'
import {
  ANALYTICS_SECTIONS,
  ANALYTICS_SECTION_ASSIGNMENT_ROLE,
  ANALYTICS_SECTION_ROLE,
  ASSIGNMENT_ROLES,
  DecisionStep,
  REFERENCE_SECTIONS,
  REFERENCE_SECTION_ROLE,
  RULES_SECTIONS,
  RULES_SECTION_ROLE,
  TIMELINE_NODES,
  WORKSPACES,
  roleForToken,
  type Role,
  type TimelineNode,
  type Workspace,
} from './constants'

/** The twelve per-tab sub-roles (owner item 29, 2026-08-28; the two Analytics
    ones, 2026-09-10), as UI role names. Derived from the three section maps so
    adding a tab is one edit, not three. */
const GRANTABLE_SECTION_ROLES: readonly Role[] = [
  ...REFERENCE_SECTIONS.map((section) => REFERENCE_SECTION_ROLE[section]),
  ...RULES_SECTIONS.map((section) => RULES_SECTION_ROLE[section]),
  // The Analytics tab's two (owner, 2026-09-10) — twelve sub-roles now, not ten.
  ...ANALYTICS_SECTIONS.map((section) => ANALYTICS_SECTION_ROLE[section]),
]

/** The company an approval appointment is scoped to. `id` is what authority
    matches on — the API matches on `company_id` and never on a name, so a
    company rename cannot move authority. `name` is display only. */
interface CompanyScope {
  id?: string
  name?: string
}

/** One active appointment, as the profile reports it. */
interface AssignmentInfo {
  id: string
  workspace: string
  role: string
  companyId?: string
  companyName?: string
  /** The catch-all expansion for THIS seat: the member companies a seat on the
      `Other` anchor also reaches. Deciding and seeing only — never appointing. */
  coversCompanyIds: string[]
}

export interface Session {
  user: CurrentUser
  /** Roles in the active workspace (see scopeSession). */
  roles: Role[]
  /** Companies the user is an active director for. LITERAL — this is the
      APPOINTMENT scope, and the Appoint pickers must keep reading it. */
  directorCompanies: CompanyScope[]
  /** Companies the user is an active department head for. LITERAL, as above. */
  departmentCompanies: CompanyScope[]
  /** Companies the user holds the Controller Approval seat for (D-303).
      LITERAL, as above. */
  controllerCompanies: CompanyScope[]
  /** Companies the user is an active `oversight` seat for. LITERAL, as above:
      the list an oversight seat may appoint `director`/`department-head`
      within and retire `department-head` within, company-exact.

      SINCE D-124 (2026-09-15) THAT IS THE SINGLE ENTRY `All`, which is not a
      company — so an org-wide oversight seat appoints nowhere, by design. It
      held real company ids during the per-company tier of 2026-08-10, which
      D-124 retired. Do not feed this list to a control that DECIDES; use
      `oversightCoveredCompanyIds` below. */
  oversightCompanies: CompanyScope[]
  /** Every company id a director seat DECIDES for, anchor expansion included.
      `canDecide` tests a record against this, never against the literal list.
      The catch-all expansion this used to name went with D-131 (2026-09-22);
      `All` is the only anchor that expands now, and only `oversight` holds it. */
  directorCoveredCompanyIds: string[]
  /** As above, for department-head seats. */
  departmentCoveredCompanyIds: string[]
  /** As above, for `controller` seats (D-303) — what `canDecide`'s Controller
      arm tests a record against. */
  controllerCoveredCompanyIds: string[]
  /** As above, for `oversight` seats. `canDecide`'s Oversight arm tests a
      record's `company_id` against this, exactly like the other two tiers —
      there is no bare "does the user hold oversight at all" flag, because
      oversight has not decided with no company clause since 2026-08-10.

      FOR A LIVE SEAT THIS IS EVERY COMPANY (D-124, 2026-09-15): the server
      expands the `All` anchor before sending it, so the reach arrives here as
      a concrete list and no screen has to know what an anchor is. A short list
      is the per-company tier of 2026-08-10, which is retired. */
  oversightCoveredCompanyIds: string[]
  rolesByWs: Record<Workspace, Role[]>
  directorCompaniesByWs: Record<Workspace, CompanyScope[]>
  departmentCompaniesByWs: Record<Workspace, CompanyScope[]>
  controllerCompaniesByWs: Record<Workspace, CompanyScope[]>
  oversightCompaniesByWs: Record<Workspace, CompanyScope[]>
  directorCoveredCompanyIdsByWs: Record<Workspace, string[]>
  departmentCoveredCompanyIdsByWs: Record<Workspace, string[]>
  controllerCoveredCompanyIdsByWs: Record<Workspace, string[]>
  oversightCoveredCompanyIdsByWs: Record<Workspace, string[]>
  /** Every active appointment, all workspaces (the Assignments gate reads it). */
  assignments: AssignmentInfo[]
  /** Companies whose records the API will show this user. Copy for UI wording
      only — row visibility is the API's WHERE clause, not this list. */
  visibleCompanyIds: string[]
  /** The company this user's own Uploader rule files an in-app upload to, or
      null. When set, the Upload window asks for no Company (D-393). */
  uploadRoutedCompany: { id: string; name: string } | null
  /** May the user see source documents? Policy, not convenience: a company
      approver never gets file access, because one upload PDF can hold invoices
      for several companies. */
  canPreviewPdf: boolean
  /** May the caller open the Assignments tab at all — the server's capability
      flag, `admin` only since 2026-10-07. */
  canViewAssignments: boolean
  /** May the user upload in the app (D-394)? The Upload button is disabled when not. */
  canUpload: boolean
  /** WHICH References tabs the server will let this session open, in tab order
      (owner item 29, 2026-08-28). EMPTY means the References screen must not be
      offered at all. A NAVIGATION HINT, never the boundary — every write route
      asks `canManageReferenceSection` again for itself. */
  referenceSections: ReferenceSection[]
  /** The same, for the Rules screen. Empty means no Rules screen. */
  rulesSections: RulesSection[]
  /** The same, for the Analytics tab (owner, 2026-09-10). Empty means no
      Analytics tab at all. */
  analyticsSections: AnalyticsSection[]
  /** The companies each Analytics tab's seats name (D-258): what that tab
      shows, and what its Company filter offers. */
  analyticsCompanies: Record<AnalyticsSection, string[]>
  isInternal: boolean
}

const emptyByWs = <T,>(value: () => T): Record<Workspace, T> =>
  Object.fromEntries(WORKSPACES.map((ws) => [ws, value()])) as Record<Workspace, T>

/** A session view scoped to one workspace, so screens and `canDecide` work on
    the active workspace without knowing there could be others. */
export function scopeSession(session: Session, workspace: Workspace): Session {
  return {
    ...session,
    roles: session.rolesByWs[workspace] ?? [],
    directorCompanies: session.directorCompaniesByWs[workspace] ?? [],
    departmentCompanies: session.departmentCompaniesByWs[workspace] ?? [],
    controllerCompanies: session.controllerCompaniesByWs[workspace] ?? [],
    oversightCompanies: session.oversightCompaniesByWs[workspace] ?? [],
    directorCoveredCompanyIds: session.directorCoveredCompanyIdsByWs[workspace] ?? [],
    departmentCoveredCompanyIds: session.departmentCoveredCompanyIdsByWs[workspace] ?? [],
    controllerCoveredCompanyIds: session.controllerCoveredCompanyIdsByWs[workspace] ?? [],
    oversightCoveredCompanyIds: session.oversightCoveredCompanyIdsByWs[workspace] ?? [],
  }
}

/** Workspaces the user can act in. A user holding only workspace-independent
    roles gets none and lands on their own page instead of an empty queue:
    `Admin` and every per-tab role put no row in anybody's queue, so a holder
    of only those would otherwise be handed an Invoices tab with nothing in it. */
const WORKSPACE_INDEPENDENT_ROLES: readonly Role[] = ['Admin', ...GRANTABLE_SECTION_ROLES]

export function availableWorkspaces(session: Session): Workspace[] {
  return WORKSPACES.filter((ws) =>
    (session.rolesByWs[ws] ?? []).some((r) => !WORKSPACE_INDEPENDENT_ROLES.includes(r)),
  )
}

/** Does the user hold this role in any workspace? For the workspace-independent
    header tabs (Admin, References). */
export function hasRole(session: Session, role: Role): boolean {
  return WORKSPACES.some((ws) => (session.rolesByWs[ws] ?? []).includes(role))
}

function isWorkspace(value: string): value is Workspace {
  return (WORKSPACES as readonly string[]).includes(value)
}

function toAssignmentInfo(a: ProfileAssignment): AssignmentInfo {
  return {
    id: a.id,
    workspace: a.workspace,
    role: a.role,
    companyId: a.company_id ?? undefined,
    companyName: a.company_name ?? undefined,
    coversCompanyIds: [...(a.covers_company_ids ?? [])],
  }
}

/** One seat's deciding reach: its own company plus, for a seat on the `Other`
    anchor, the member companies the server expanded it to. */
function coveredIdsOf(a: AssignmentInfo): string[] {
  const out: string[] = []
  if (a.companyId) out.push(a.companyId)
  for (const id of a.coversCompanyIds) if (id) out.push(id)
  return out
}

/** Builds the session from the profile payload. Exported so the shell can
    re-derive after a role change without another sign-in. */
export function sessionFromProfile(profile: Profile): Session {
  const user: CurrentUser = {
    fullName: profile.display_name || profile.upn || 'Unknown user',
    email: profile.upn ?? '',
    userId: profile.user_id ?? '',
    directoryEmail: profile.directory_email ?? undefined,
  }

  const rolesByWs = emptyByWs<Role[]>(() => [])
  const directorCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const departmentCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const controllerCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const oversightCompaniesByWs = emptyByWs<CompanyScope[]>(() => [])
  const directorCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const departmentCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const controllerCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())
  const oversightCoveredByWs = emptyByWs<Set<string>>(() => new Set<string>())

  const seatCompanies: Record<AnalyticsSection, string[]> = { dashboard: [], reporting: [] }

  for (const [ws, tokens] of Object.entries(profile.roles_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const token of tokens) {
      // A token the app does not know maps to nothing and grants nothing (D-293).
      const role = roleForToken(token)
      if (role && !rolesByWs[ws].includes(role)) rolesByWs[ws].push(role)
    }
  }

  const assignments = (profile.assignments ?? []).map(toAssignmentInfo)

  // Approval authority is data, not membership: an appointment row IS the
  // permission. A row for a workspace this build does not know about is skipped
  // rather than crashing — historical rows outlive a workspace being retired.
  for (const a of assignments) {
    if (!isWorkspace(a.workspace)) continue
    const ws = a.workspace
    const scope: CompanyScope = { id: a.companyId, name: a.companyName }
    if (a.role === ASSIGNMENT_ROLES.director) {
      if (!rolesByWs[ws].includes('DirectorApproval')) rolesByWs[ws].push('DirectorApproval')
      directorCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) directorCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.departmentHead) {
      if (!rolesByWs[ws].includes('DepartmentApproval')) rolesByWs[ws].push('DepartmentApproval')
      departmentCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) departmentCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.controller) {
      // The Controller Approval seat (D-303): one company, like department-head.
      if (!rolesByWs[ws].includes('ControllerApproval')) rolesByWs[ws].push('ControllerApproval')
      controllerCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) controllerCoveredByWs[ws].add(id)
    } else if (a.role === ASSIGNMENT_ROLES.oversight) {
      // Company-scoped since 2026-08-10, exactly like director and
      // department-head above — no longer "organization-wide, no company
      // scope recorded".
      if (!rolesByWs[ws].includes('OversightApproval')) rolesByWs[ws].push('OversightApproval')
      oversightCompaniesByWs[ws].push(scope)
      for (const id of coveredIdsOf(a)) oversightCoveredByWs[ws].add(id)
    } else {
      // An Analytics seat (D-258) lights its own tab and nothing else.
      for (const section of ANALYTICS_SECTIONS) {
        if (a.role !== ANALYTICS_SECTION_ASSIGNMENT_ROLE[section]) continue
        const role = ANALYTICS_SECTION_ROLE[section]
        if (!rolesByWs[ws].includes(role)) rolesByWs[ws].push(role)
        if (a.companyId && !seatCompanies[section].includes(a.companyId)) seatCompanies[section].push(a.companyId)
      }
    }
  }

  // The server sends the expanded lists per workspace as well (§1.1), and they are
  // the authority; the per-seat sets above say the same thing and cover a payload
  // that carries only one of the two. Union, so neither can narrow the other.
  for (const [ws, ids] of Object.entries(profile.director_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) directorCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.department_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) departmentCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.controller_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) controllerCoveredByWs[ws].add(id)
  }
  for (const [ws, ids] of Object.entries(profile.oversight_covered_companies_by_workspace ?? {})) {
    if (!isWorkspace(ws)) continue
    for (const id of ids) if (id) oversightCoveredByWs[ws].add(id)
  }
  const directorCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const departmentCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const controllerCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  const oversightCoveredCompanyIdsByWs = emptyByWs<string[]>(() => [])
  for (const ws of WORKSPACES) {
    directorCoveredCompanyIdsByWs[ws] = [...directorCoveredByWs[ws]]
    departmentCoveredCompanyIdsByWs[ws] = [...departmentCoveredByWs[ws]]
    controllerCoveredCompanyIdsByWs[ws] = [...controllerCoveredByWs[ws]]
    oversightCoveredCompanyIdsByWs[ws] = [...oversightCoveredByWs[ws]]
  }

  const [first] = WORKSPACES
  const capabilities = profile.capabilities
  return {
    user,
    roles: rolesByWs[first] ?? [],
    directorCompanies: directorCompaniesByWs[first] ?? [],
    departmentCompanies: departmentCompaniesByWs[first] ?? [],
    controllerCompanies: controllerCompaniesByWs[first] ?? [],
    oversightCompanies: oversightCompaniesByWs[first] ?? [],
    directorCoveredCompanyIds: directorCoveredCompanyIdsByWs[first] ?? [],
    departmentCoveredCompanyIds: departmentCoveredCompanyIdsByWs[first] ?? [],
    controllerCoveredCompanyIds: controllerCoveredCompanyIdsByWs[first] ?? [],
    oversightCoveredCompanyIds: oversightCoveredCompanyIdsByWs[first] ?? [],
    rolesByWs,
    directorCompaniesByWs,
    departmentCompaniesByWs,
    controllerCompaniesByWs,
    oversightCompaniesByWs,
    directorCoveredCompanyIdsByWs,
    departmentCoveredCompanyIdsByWs,
    controllerCoveredCompanyIdsByWs,
    oversightCoveredCompanyIdsByWs,
    assignments,
    visibleCompanyIds: profile.visible_company_ids ?? [],
    uploadRoutedCompany: profile.upload_routed_company
      ? { id: profile.upload_routed_company.company_id, name: profile.upload_routed_company.company_name }
      : null,
    canPreviewPdf: !!capabilities?.can_preview_pdf,
    canViewAssignments: !!capabilities?.can_view_assignments,
    // FAIL CLOSED: a profile that does not say so cannot upload (D-394).
    canUpload: !!capabilities?.can_upload,
    // FAIL CLOSED on a payload that carries neither key: no sections means no
    // screen, which refuses rather than offers.
    referenceSections: [...(capabilities?.reference_sections ?? [])],
    rulesSections: [...(capabilities?.rules_sections ?? [])],
    analyticsSections: [...(capabilities?.analytics_sections ?? [])],
    // The server's lists when it sends them; the seats themselves otherwise.
    analyticsCompanies: {
      dashboard: [...(capabilities?.analytics_companies?.dashboard ?? seatCompanies.dashboard)],
      reporting: [...(capabilities?.analytics_companies?.reporting ?? seatCompanies.reporting)],
    },
    isInternal: !!capabilities?.is_internal,
  }
}

/** Loads the profile. Throws on failure — the caller renders an error state
    rather than a usable UI (see the fail-closed rule above). */
export async function loadSession(): Promise<Session> {
  const profile = await getClient().getProfile()
  return sessionFromProfile(profile)
}

/** The server's own capability flag: `admin` only (2026-10-07). HALF of the
    real gate, so this alone must never decide whether the tab renders. Use
    `canOpenAssignments` (below) for that; this export exists for that
    function and for the tests that pin the two halves' relationship. */
export function canViewAssignments(session: Session): boolean {
  return session.canViewAssignments
}

/**
 * One of the Assignments tab's screens. Order is significant: it is the order
 * the tab lists them in.
 *
 * SINCE 2026-09-14 THESE MIRROR THE APP'S OWN NAV TABS (owner), one for one and
 * in the same order. The question a screen answers is now "which tab of the app
 * does this role let someone use?", which is the question an admin is actually
 * holding when they open this — rather than the previous split by where the
 * role came from in the migration (`admin` / `data` / `accounting` / `approval`,
 * which meant an appointer had to know that Import Control was "accounting" and
 * Vendors was "data"). Nothing persists these values; they are the tab's own
 * state, so the rename is a rename.
 */
export type AssignmentScreen = 'invoices' | 'notifications' | 'analytics' | 'references' | 'rules' | 'assignments'

/**
 * Which of the Assignments tab's screens this session may open: every one for
 * `admin`, and none for anybody else (owner, 2026-10-07: "Let's simplify
 * Assignments by just making every assignment there admin-only."). Until then
 * an internal step role or any active assignment opened the Invoices screen,
 * read-only where the viewer could not appoint.
 *
 * Gated on the literal `hasRole(session, 'Admin')`, never derived from a group
 * list, so holding a role that is appointed here never opens the screen that
 * appoints it.
 */
export function visibleAssignmentScreens(session: Session): AssignmentScreen[] {
  if (!hasRole(session, 'Admin')) return []
  return ['invoices', 'notifications', 'analytics', 'references', 'rules', 'assignments']
}

/**
 * The reads one Assignments screen is waiting on, as `ReadState`s.
 *
 * Only `invoices` has more than one source: the `user_roles` roster behind its
 * five centralized step roles, plus the assignments table and the company
 * master behind its per-company tiers. Every screen is Admin-only
 * (`visibleAssignmentScreens`), and both lists are Admin-only on the server
 * (code review G-05, 2026-10-17), so every screen reads the roster.
 */
export function assignmentScreenReads<T>(
  screen: AssignmentScreen,
  reads: { userRoles: T; assignments: T; companies: T },
): T[] {
  // Notifications reads its own lists (`NotificationRecipientsManager`), none of these.
  if (screen === 'notifications') return []
  if (screen !== 'invoices') return [reads.userRoles]
  return [reads.userRoles, reads.assignments, reads.companies]
}

/**
 * The ONE gate for "does the Assignments tab actually open" — both halves,
 * ANDed: the server's own capability flag AND at least one screen the client
 * can derive. Both answer "is this an administrator" today; the AND keeps the
 * client from opening the tab on its own reading alone. Use
 * this everywhere the question is "should the tab exist" — the landing page,
 * the nav tab, and the screen's own render guard — rather than letting each
 * site reinvent the AND and risk one of them trusting a single half.
 */
export function canOpenAssignments(session: Session): boolean {
  return canViewAssignments(session) && visibleAssignmentScreens(session).length > 0
}

/**
 * May the caller open the Analytics tab at all — an Analytics seat for at
 * least one company (D-258). Mirrors `canOpenAssignments`: one named
 * predicate, asked once, so the nav tab and the render guard cannot drift
 * into asking the question two different ways.
 *
 * Unlike Assignments, holding the role is the whole answer here — there is no
 * second, server-computed capability flag to AND against, and no per-screen
 * derivation.
 *
 * PER COMPANY SINCE 2026-10-01 (owner, `docs/decisions.md` D-258): the tab is
 * lit by a Dashboard or Reporting appointment for a company, and each tab shows
 * exactly its seats' companies (`session.analyticsCompanies`). From 2026-09-11
 * until then the three Analytics roles were cross-company (D-98).
 */
/**
 * Admin → Errors (owner, 2026-09-14): the Admin role and nothing else. The
 * server enforces the same gate on both `/ops` reads (`requireAdmin`), so this
 * only decides whether the tab is drawn. Not a fifth Assignments screen — that
 * strip lists the app's tabs for APPOINTING roles, and "appoint to Errors" is
 * not a thing (docs/plans/2026-09-14-admin-errors-page.md §7).
 */
export function canOpenErrors(session: Session): boolean {
  return hasRole(session, 'Admin')
}

export function canOpenAnalytics(session: Session): boolean {
  return visibleAnalyticsTabs(session).length > 0
}

/**
 * Which Analytics sub-tabs to render, in tab order. Empty means no tab at all.
 *
 * THE SERVER'S ANSWER FIRST, the coarse role as the fallback — the same shape
 * `visibleRuleTabs` uses, and for the same reason: a bundle left open across a
 * deploy reads a payload with no `analytics_sections` key, and must not lose
 * the tab it already had.
 */
export function visibleAnalyticsTabs(session: Session): AnalyticsSection[] {
  if (session.analyticsSections.length > 0) return [...session.analyticsSections]
  // Otherwise the tab roles `sessionFromProfile` derived from the seats
  // (D-258). The retired coarse `Analytics` role opens nothing any more.
  // `Admin` is DELIBERATELY not here: the 2026-08-14 owner ruling withdrew
  // invoice and document access from it (`canViewAnalyticsSection`,
  // backend/src/domain/authority.ts).
  return ANALYTICS_SECTIONS.filter((section) =>
    hasRole(session, ANALYTICS_SECTION_ROLE[section]),
  )
}

// ---- References / Rules, per tab (owner item 29, 2026-08-28) ---------------
//
// Each tab has its own role, and a user sees only the tabs they hold one for —
// and sees the top-level screen only if they hold at least one. `GET /me` sends
// `reference_sections` / `rules_sections`, computed by `referenceSectionsFor`
// / `rulesSectionsFor` in `backend/src/domain/authority.ts` — one question,
// asked once, on the side that enforces it. There is no client-side fallback.
//
// A HINT, NEVER THE BOUNDARY: every write route re-asks the same question
// through `canManageReferenceSection` / `canManageRulesSection`.

/** Which References tabs to render, in tab order. Empty means no screen. */
export function visibleReferenceTabs(session: Session): ReferenceSection[] {
  return [...session.referenceSections]
}

/** Which Rules tabs to render, in tab order. Empty means no screen. */
export function visibleRuleTabs(session: Session): RulesSection[] {
  return [...session.rulesSections]
}

/** May the caller open the References screen at all? At least one tab. */
export function canOpenReferences(session: Session): boolean {
  return visibleReferenceTabs(session).length > 0
}

/** May the caller open the Rules screen at all? At least one tab. */
export function canOpenRules(session: Session): boolean {
  return visibleRuleTabs(session).length > 0
}

/** Where `App.tsx` lands a user with no pipeline node lit. Pulled out as a
    pure function so the order is testable without rendering the shell:
    References before Assignments before the dashboard. That order matters
    because the widened Assignments capability (any role, any active
    assignment) now covers a References-only user too, and they must land on
    the screen they actually work, not on Assignments. */
export function landingPage(
  session: Session,
): 'training' | 'rules' | 'assignments' | 'dashboard' | null {
  // Any References tab at all lands here, not just the coarse role: a holder of
  // `references_marketing` alone works the References screen and nothing else
  // (owner item 29, 2026-08-28).
  if (canOpenReferences(session)) return 'training'
  // RULES IS A LANDING SECTION TOO (QA wave A, A-03). It was missing, so a
  // Rules-only identity — `?teams=rules_oversight&assign=none` is the one the
  // sweep drove — fell through to `dashboard`, a page that identity cannot
  // open: the header rendered its single `Rules` tab and the body rendered
  // nothing at all until the tab was clicked. The arms are in nav order
  // (`App.tsx`), so the section somebody lands on is the first one they have.
  if (canOpenRules(session)) return 'rules'
  if (canOpenAssignments(session)) return 'assignments'
  if (canOpenAnalytics(session)) return 'dashboard'
  // NOBODY'S PAGE (QA wave B2, B2-03). This used to return `dashboard`
  // unconditionally as a last resort, on the reasoning that every real identity
  // holds at least one of the four. That is not true on the day an account is
  // created: a person who has been given nothing yet got `dashboard` without
  // `canOpenAnalytics`, and `App.tsx` renders that combination as NOTHING —
  // brand bar, version number, white page, no explanation. Answering `null`
  // hands them the shell's own named empty state instead, which is the rule
  // every other "nothing here" in this app follows.
  //
  // `dashboard` is now reached the same way the other three are — by holding the
  // section — rather than by falling off the end.
  return null
}

/** The document-access policy, straight from the profile's capability flag. */
export function canPreviewPdf(session: Session): boolean {
  return session.canPreviewPdf
}

// ---- Decision rights --------------------------------------------------------
// A hint that mirrors the API's gate (api-contract §6.2): an internal step is
// its role; the three approval tiers are an active appointment that COVERS the
// record's company.
//
// "Covers" is the expanded answer, not the literal one. A seat on the `Other`
// anchor decides for the anchor AND for its member companies, because the
// `… - Other` team owned their records — the server computes that expansion and
// sends it as `*_covered_companies_by_workspace`. Testing the literal seat list
// instead hid the Approve button on a member company's invoice the API would have
// accepted, leaving the record unactionable for the only person who could act.
// APPOINTMENT authority is the other half of the same rule and stays literal (the
// 2026-08-03 broker ruling): the Appoint pickers read `directorCompanies`, which
// is why that list is kept unexpanded rather than replaced.

const STEP_ROLE: Partial<Record<DecisionStep, Role>> = {
  [DecisionStep.ImportControl]: 'ImportControl',
  [DecisionStep.ExportControl]: 'ExportControl',
  [DecisionStep.ClosingConfirmation]: 'ClosingConfirmation',
  [DecisionStep.DenialReview]: 'DenialReview',
}

export function canDecide(session: Session, step: DecisionStep, row: Invoice): boolean {
  // Oversight takes an active `oversight` APPOINTMENT and nothing else. There is
  // no `oversight_approval` user_role (api-contract §0.3): the appointment alone
  // carries the tier's authority and the document access, so requiring a second
  // grant would disable a control the API allows. It does NOT carry cross-company
  // visibility — that ended with the NULL company on 2026-08-10, as the next line
  // says; the two statements contradicted each other until then.
  // COMPANY-SCOPED since 2026-08-10, exactly like Director and Department below —
  // `oversightCoveredCompanyIds` is the scoped, catch-all-expanded field
  // `scopeSession` sets for the active workspace, mirroring the other two tiers
  // exactly. There is no longer a bare "holds oversight anywhere" flag.
  if (step === DecisionStep.Oversight) {
    return coversCompany(session.oversightCoveredCompanyIds, row)
  }
  if (step === DecisionStep.Director) {
    return coversCompany(session.directorCoveredCompanyIds, row)
  }
  if (step === DecisionStep.Department) {
    return coversCompany(session.departmentCoveredCompanyIds, row)
  }
  // Controller Approval (D-303): the `controller` seat for the record's company.
  if (step === DecisionStep.Controller) {
    return coversCompany(session.controllerCoveredCompanyIds, row)
  }
  const role = STEP_ROLE[step]
  return !!role && session.roles.includes(role)
}

/** Company match by id, exactly as the API does it. An empty id covers nothing:
    the API's `company_id = $1` would not match it either. */
function coversCompany(companyIds: readonly string[], row: Invoice): boolean {
  const companyId = row.company_id ?? ''
  return !!companyId && companyIds.includes(companyId)
}

/**
 * Narrows a queue to the rows this session can actually decide at `step` —
 * every decision screen must show only what its user can act on (owner
 * ruling, 2026-08-14): "a user cannot see or act on any invoices outside his
 * role. Even a Director would not have any visibility into his company's
 * AP/AR and Department steps — only his own." A row outside the caller's own
 * step disappears from the queue rather than rendering view-only — there is
 * no third state — mirroring the retired AP/AR Approval screen's original
 * queue filter, which every decision screen now follows through this one
 * function instead of five separate copies.
 *
 * This is a CLIENT convenience, not the security boundary: the server's
 * company partition and step authority (`docs/security.md` §4, §4.6) are
 * unchanged and remain company-scoped, not step-scoped — narrowing them
 * would break Archives' "released records stay visible forever" rule and
 * would make Analytics undercount a company's own pipeline, since Analytics
 * reads through the identical row-level-security predicate. What this
 * function closes is every path a signed-in user reaches THROUGH THE APP; a
 * direct API call still reaches what the server always granted.
 *
 * A row a caller CAN decide but that is currently on HOLD still shows: a
 * hold is a temporal state, not a role question, and stays each screen's own
 * concern (the lock icon, the disabled Approve/Deny pair).
 */
export function filterDecidable(session: Session, step: DecisionStep, invoices: readonly Invoice[]): Invoice[] {
  return invoices.filter((inv) => canDecide(session, step, inv))
}

/**
 * The queue screen a read-only invoice modal's "Go To <Step>" button would open,
 * or `null` when there is none to offer (owner, 2026-09-30, `docs/decisions.md`
 * D-252: *"should only be visible if the user has access to the step being
 * referenced, accounting also for the invoice's company"*).
 *
 * Two questions, both asked:
 *  1. Is the node this invoice is sitting at one this session can open at all?
 *     `lit` is the same lit-role set the Pipeline dialog and the page tabs use.
 *  2. Would that node's queue LIST this invoice for this session? For a step
 *     node that is `filterDecidable` — the exact filter every step screen runs
 *     over its rows (above) — asked of this one invoice. So a Director seat on
 *     SeniorLIFE Johnstown gets no button on a SeniorLIFE Altoona invoice at
 *     Director Approval, while an Import Control role (organization-wide) and
 *     the Oversight seat (organization-wide, D-124) keep theirs. Archives has
 *     no step and no client filter of its own: it lists what the server's
 *     company partition admits, the same partition that let this caller read
 *     the invoice here, so the role check is the whole answer there.
 *
 * `session` must be the workspace-scoped session (`scopeSession`), as the
 * queue badges use. A convenience, not a gate: the queue screen and the server
 * decide again.
 */
export function goToNodeFor(session: Session, lit: ReadonlySet<Role>, invoice: Invoice): TimelineNode | null {
  const node = TIMELINE_NODES.find((n) => n.queueStatuses.some((st) => st === invoice.status))
  if (!node || !lit.has(node.role)) return null
  if (node.step && filterDecidable(session, node.step, [invoice]).length === 0) return null
  return node
}
